import React, { useEffect, useState } from "react";
import { withRouter } from "react-router-dom";
import { Download } from "react-feather";
import * as XLSX from "xlsx";
import queryString from "query-string";

import useCallApi from "../../hooks/useCallApi";
import useDateRangeFilter from "../../hooks/useDateRangeFilter";

import DateRangeFilter from "../../component/Filters/DateRangeFilter/DateRangeFilter";
import Loading from "../../component/UI/Loading/Loading";

import ByCountry from "./ByCountry/ByCountry";
import ByRegion from "./ByRegion/ByRegion";
import ByCity from "./ByCity/ByCity";
import ByDeviceOS from "./ByDeviceOS/ByDeviceOS";
import ByCreative from "./ByCreative/ByCreative";
import ByLandingPage from "./ByLandingPage/ByLandingPage";
import ByUserAgent from "./ByUserAgent/ByUserAgent";
import ByTrafficTypes from "./ByTrafficTypes/ByTrafficTypes";
import ByTimeOfDay from "./ByTimeOfDay/ByTimeOfDay";
import { Link } from "react-router-dom/cjs/react-router-dom";

function autoWidth(sheet) {
  const cols = sheet.reduce((acc, row) => {
    row.forEach((cell, i) => {
      const len = cell ? cell.toString().length : 10;
      acc[i] = Math.max(acc[i] || 10, len);
    });
    return acc;
  }, []);

  return cols.map((w) => ({ wch: w + 5 }));
}

function buildSheetData(data) {
  if (!data) return { sheet1: [], sheet2: [], sheet3: [] };

  const {
    geographic = [],
    region = [],
    city = [],
    device_os = [],
    user_agent = [],
    traffic_types = [],
    time_of_day = [],
    landing_pages = [],
    creatives = [],
    total_leads = 0,
    total_leads_from_stats = 0,
    total_leads_from_lp_stats = 0,
  } = data;

  const fmtPct = (num) =>
    total_leads > 0 ? parseFloat((num / total_leads) * 100).toFixed(2) : "0.00";

  const fmtPctStats = (num, total) =>
    total > 0 ? parseFloat((num / total) * 100).toFixed(2) : "0.00";

  const getHours = (timeOfDayString) => {
    if (timeOfDayString === "1-Overnight") return "10 PM - 5 AM";
    if (timeOfDayString === "2-Morning") return "5 AM - 12 PM";
    if (timeOfDayString === "3-Afternoon") return "12 PM - 5 PM";
    if (timeOfDayString === "4-Evening") return "5 PM - 10 PM";
    return "Unknown";
  };

  // Sheet 1: By Country + By Region + By City
  const sheet1 = [];
  sheet1.push(["By Country"]);
  sheet1.push(["Country", "Lead %", "Lead Count"]);
  if (geographic.length === 0) sheet1.push(["No stats available"]);
  else geographic.forEach((item) => sheet1.push([item.ip_country, fmtPct(item.lead_count), item.lead_count]));
  sheet1.push([]);

  sheet1.push(["By Region"]);
  sheet1.push(["Country", "Region", "Lead %", "Lead Count"]);
  if (region.length === 0) sheet1.push(["No stats available"]);
  else region.forEach((item) => sheet1.push([item.ip_country, item.ip_region, fmtPct(item.lead_count), item.lead_count]));
  sheet1.push([]);

  sheet1.push(["By City (Top 10)"]);
  sheet1.push(["City", "Region", "Country", "Lead %", "Lead Count"]);
  if (city.length === 0) sheet1.push(["No stats available"]);
  else city.forEach((item) => sheet1.push([item.ip_city, item.ip_region, item.ip_country, fmtPct(item.lead_count), item.lead_count]));

  // Sheet 2: Device OS + Top 10 User Agents
  const sheet2 = [];
  sheet2.push(["Device Operating Systems"]);
  sheet2.push(["Device OS", "Lead %", "Lead Count"]);
  if (device_os.length === 0) sheet2.push(["No stats available"]);
  else device_os.forEach((item) => sheet2.push([item.device_os, fmtPct(item.lead_count), item.lead_count]));
  sheet2.push([]);

  sheet2.push(["Top 10 User Agents"]);
  sheet2.push(["User Agent", "Lead %", "Lead Count"]);
  if (user_agent.length === 0) sheet2.push(["No stats available"]);
  else user_agent.forEach((item) => sheet2.push([item.user_agent, fmtPct(item.lead_count), item.lead_count]));

  // Sheet 3: rest (Traffic Type, Time of Day, Landing Pages, Creatives)
  const sheet3 = [];
  sheet3.push(["By Traffic Type"]);
  sheet3.push(["Traffic Type", "Leads"]);
  if (traffic_types.length === 0) sheet3.push(["No stats available"]);
  else traffic_types.forEach((item) => sheet3.push([item.traffic_type, item.traffic_count]));
  sheet3.push([]);

  sheet3.push(["Time of Day"]);
  sheet3.push(["Period", "Lead %", "Lead Count"]);
  if (time_of_day.length === 0) sheet3.push(["No stats available"]);
  else time_of_day.forEach((item) => sheet3.push([`${item.time_of_day} (${getHours(item.time_of_day)})`, fmtPct(item.time_of_day_count), item.time_of_day_count]));
  sheet3.push([]);

  sheet3.push(["By Landing Page"]);
  sheet3.push(["Landing Page ID", "Landing Page Name", "Lead %", "EPC", "Lead Count"]);
  if (landing_pages.length === 0) sheet3.push(["No stats available"]);
  else landing_pages.forEach((item) => sheet3.push([item.lp_record_id, item.lp_name, fmtPctStats(item.lead_count, total_leads_from_lp_stats), item.epc, item.lead_count]));
  sheet3.push([]);

  sheet3.push(["By Creative"]);
  sheet3.push(["Creative ID", "Type", "Size", "Lead %", "EPC", "Lead Count"]);
  if (creatives.length === 0) sheet3.push(["No stats available"]);
  else creatives.forEach((item) => {
    const size = item.width && item.height ? `${item.width}x${item.height}` : "";
    sheet3.push([item.creative_id, item.creative_type, size, fmtPctStats(item.lead_count, total_leads_from_stats), item.epc, item.lead_count]);
  });

  return { sheet1, sheet2, sheet3 };
}

function downloadTrafficAnalyticsExcel(data, campaignId) {
  const { sheet1, sheet2, sheet3 } = buildSheetData(data);
  const wb = XLSX.utils.book_new();
  const ws1 = XLSX.utils.aoa_to_sheet(sheet1);
  const ws2 = XLSX.utils.aoa_to_sheet(sheet2);
  const ws3 = XLSX.utils.aoa_to_sheet(sheet3);

  ws1["!cols"] = autoWidth(sheet1);
  ws2["!cols"] = autoWidth(sheet2);
  ws3["!cols"] = autoWidth(sheet3);

  XLSX.utils.book_append_sheet(wb, ws1, "Geography");
  XLSX.utils.book_append_sheet(wb, ws2, "Devices & User Agents");
  XLSX.utils.book_append_sheet(wb, ws3, "Other Analytics");
  XLSX.writeFile(wb, `Traffic Analytics - Campaign ${campaignId}.xlsx`);
}

const TrafficAnalytics = ({ showSystemMessage, match, history, location }) => {
  const campaignId = parseInt(match.params.campaignId);

  const {
    selectedStartDate,
    selectedEndDate,
    quickDate,
    handleChangeStart,
    handleChangeEnd,
    handleQuickDate,
  } = useDateRangeFilter(undefined, false, location.search);

  const [reportStartDate, setReportStartDate] = useState(selectedStartDate);
  const [reportEndDate, setReportEndDate] = useState(selectedEndDate);

  useEffect(() => {
    const params = queryString.parse(location.search);
    if (params.startDate && params.endDate) {
      setReportStartDate(params.startDate);
      setReportEndDate(params.endDate);
      return;
    }

    setReportStartDate(selectedStartDate);
    setReportEndDate(selectedEndDate);
  }, [location.search]);

  const apiUrl = `reports/trafficAnalytics?oid=${campaignId}&startDate=${reportStartDate}&endDate=${reportEndDate}`;
  const { dataLoaded, data } = useCallApi(
    apiUrl,
    "GET",
    {},
    [campaignId, reportStartDate, reportEndDate],
    showSystemMessage,
  );

  useEffect(() => {
    document.title =
      dataLoaded && data && data.campaign
        ? `${data.campaign.name}: Traffic Analytics`
        : "Traffic Analytics";
  }, [dataLoaded, data]);

  const refreshReport = (e) => {
    e.preventDefault();
    setReportStartDate(selectedStartDate);
    setReportEndDate(selectedEndDate);
    history.push(
      `/campaign/${campaignId}/analytics?startDate=${selectedStartDate}&endDate=${selectedEndDate}`,
    );
  };

  return (
    <div>
      <div className="py-2">
        <div className="card">
          <div className="card-header d-flex align-items-center">
              <div className="flex-1">Traffic Analytics</div>
              <div>
                  <button
                    type="button"
                    className="btn btn-sm btn-success ml-auto"
                    disabled={!dataLoaded || !data}
                    onClick={() => downloadTrafficAnalyticsExcel(data, campaignId)}
                  >
                    <Download size={14} /> Download Excel
                  </button>
              </div>
            </div>
            <div className="card-body">
              {dataLoaded && data && (                
                  <div className="d-flex align-items-center">
                    <h1 className="mb-h1-dark mr-2">
                      {data.campaign ? <Link to={`/campaign/${data.campaign.campaign_id}`}>{data.campaign.name}</Link> : <Link to={`/campaign/${data.campaign.campaign_id}`}>Campaign {campaignId}</Link>}
                    </h1>
                    <DateRangeFilter
                      quickDate={quickDate}
                      selectedStartDate={selectedStartDate}
                      selectedEndDate={selectedEndDate}
                      handleChangeStart={handleChangeStart}
                      handleChangeEnd={handleChangeEnd}
                      handleQuickDate={handleQuickDate}
                    />
                    <button
                      onClick={refreshReport}
                      className="btn btn-sm btn-primary ml-2"
                    >
                      Refresh Report
                    </button>
                  </div>                
              )}
            </div>
        </div>
      </div>
      <div>
        {dataLoaded && data ? (
          <div className="row Affiliate">
            <div className="col-xl-6">
              <ByCountry
                geographic={data.geographic || []}
                totalLeads={data.total_leads || 0}
              />
              <ByRegion
                region={data.region || []}
                totalLeads={data.total_leads || 0}
              />
              <ByCity
                city={data.city || []}
                totalLeads={data.total_leads || 0}
              />
              <ByDeviceOS
                deviceOS={data.device_os || []}
                totalLeads={data.total_leads || 0}
              />
            </div>
            <div className="col-xl-6 pb-0">
              <ByCreative
                creatives={data.creatives || []}
                totalLeadsFromStats={data.total_leads_from_stats || 0}
              />
              <ByLandingPage
                landingPages={data.landing_pages || []}
                totalLeadsFromLpStats={data.total_leads_from_lp_stats || 0}
              />
              <ByUserAgent
                userAgents={data.user_agent || []}
                totalLeads={data.total_leads || 0}
              />
              <ByTrafficTypes trafficTypes={data.traffic_types || []} />
              <ByTimeOfDay
                timeOfDay={data.time_of_day || []}
                totalLeads={data.total_leads || 0}
              />
            </div>
          </div>
        ) : (
          <Loading />
        )}
      </div>
    </div>
  );
};

export default withRouter(TrafficAnalytics);
